import type {CustomPropertiesSchemaPagePayload} from '@github-ui/custom-properties-types'
import {customPropertyDetailsPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Box, Button, Label} from '@primer/react'

import {DefinitionsLimitBanner, isDefinitionsLimitReached} from '../components/Banners'
import {DefinitionsList} from '../components/DefinitionsList'
import {PageHeader} from '../components/PageHeader'
import {PropertiesPageTabs} from '../components/PropertiesPageTabs'
import {SetValuesTab} from '../components/SetValuesTab'
import {CurrentOrgRepoProvider, useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useActiveTab} from '../hooks/use-active-tab'

export function CustomPropertiesSchemaPage() {
  return (
    <CurrentOrgRepoProvider>
      <CustomPropertiesSchemaPageContent />
    </CurrentOrgRepoProvider>
  )
}

function CustomPropertiesSchemaPageContent() {
  const payload = useRoutePayload<CustomPropertiesSchemaPagePayload>()
  const {definitions, pageCount, repositories, repositoryCount, permissions} = payload

  const [activeTab, hrefBuilder] = useActiveTab(permissions)

  const definitionLimitReached = isDefinitionsLimitReached(definitions)
  const canCreateProperty = !definitionLimitReached && (permissions === 'all' || permissions === 'definitions')

  return (
    <>
      <Header canCreateProperty={canCreateProperty} />
      {definitionLimitReached && (
        <Box sx={{mb: 2}}>
          <DefinitionsLimitBanner />
        </Box>
      )}
      {permissions === 'all' && (
        <PropertiesPageTabs activeTab={activeTab} definitionsCount={definitions.length} hrefBuilder={hrefBuilder} />
      )}
      {activeTab === 'set-values' ? (
        <SetValuesTab definitions={definitions} initialResults={{repositories, pageCount, repositoryCount}} />
      ) : (
        <DefinitionsList definitions={definitions} />
      )}
    </>
  )
}

function Header({canCreateProperty}: {canCreateProperty: boolean}) {
  const org = useCurrentOrg()

  return (
    <PageHeader
      title={
        <Box sx={{display: 'flex', alignItems: 'center'}}>
          <Box as="h2" sx={{fontWeight: 400}}>
            Custom properties
          </Box>
          <Label variant="success" sx={{verticalAlign: 'middle', ml: 2}}>
            Beta
          </Label>
        </Box>
      }
      subtitle="Custom properties allow you to decorate your repositories with information such as compliance frameworks, data
      sensitivity, or project details."
      actions={
        canCreateProperty && (
          <Button
            as={Link}
            to={customPropertyDetailsPath({org: org.login})}
            variant="primary"
            data-testid="add-definition-button"
          >
            New property
          </Button>
        )
      }
    />
  )
}

try{ CustomPropertiesSchemaPage.displayName ||= 'CustomPropertiesSchemaPage' } catch {}
try{ CustomPropertiesSchemaPageContent.displayName ||= 'CustomPropertiesSchemaPageContent' } catch {}
try{ Header.displayName ||= 'Header' } catch {}